"use client";

import * as React from "react";
import { Clipboard, FolderOpen, Link, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatFileSize } from "../../hooks/useUserLimits";
import {
  isExternalSource,
  isURLSource,
  UploadableSource,
  UploadableURL,
} from "../../types";
import { useAuth } from "@/lib/auth";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type InvalidFileReason = "size" | "type";

export type InvalidFile = {
  file: File;
  reason: InvalidFileReason;
  message: string;
};

interface ExternalSourcesTabProps {
  allowedMimeTypes: string[];
  allowedFileExtensions: string[];
  maxFileSizeBytes: number;

  selectedSources: UploadableSource[];
  setSelectedSources: React.Dispatch<React.SetStateAction<UploadableSource[]>>;

  invalidFiles: InvalidFile[];
  setInvalidFiles: React.Dispatch<React.SetStateAction<InvalidFile[]>>;

  onOpenLinkDialog?: () => void;
  onOpenPasteTextDialog?: () => void;
}

export function ExternalSourcesTab({
  allowedFileExtensions,
  allowedMimeTypes,
  maxFileSizeBytes,
  selectedSources,
  setSelectedSources,
  invalidFiles,
  setInvalidFiles,
  onOpenLinkDialog,
  onOpenPasteTextDialog,
}: ExternalSourcesTabProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = React.useState(false);

  const selectedLinks = selectedSources.filter(isURLSource) as UploadableURL[];
  const selectedFiles = selectedSources.filter(isExternalSource) as File[];

  const { planType } = useAuth();

  const validateFile = (file: File): InvalidFile | null => {
    if (!allowedMimeTypes.includes(file.type)) {
      return {
        file,
        reason: "type",
        message: `"${file.name}" is not a supported file type`,
      };
    }

    if (file.size > maxFileSizeBytes) {
      return {
        file,
        reason: "size",
        message: `"${file.name}" exceeds the ${(
          maxFileSizeBytes /
          1024 /
          1024
        ).toFixed(0)}MB limit`,
      };
    }

    return null;
  };

  const addFiles = (files: File[]) => {
    const nextValid: File[] = [];
    const nextInvalid: InvalidFile[] = [];

    files.forEach((file) => {
      const invalid = validateFile(file);
      if (invalid) {
        nextInvalid.push(invalid);
      } else {
        nextValid.push(file);
      }
    });

    nextInvalid.forEach((i) =>
      toast.error("Invalid file", {
        description: <span className="text-theme-1">{i.message}</span>,
      })
    );

    setInvalidFiles((prev) => [...prev, ...nextInvalid]);
    setSelectedSources((prev) => [...prev, ...nextValid]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (file: File) => {
    setSelectedSources((prev) => prev.filter((x) => x != file));
  };

  return (
    <div className="space-y-2 w-full">
      <div
        className={`bg-theme-1 border-2 border-dashed transition-colors rounded-2xl p-2 ${
          !dragActive && "border-theme"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept={allowedMimeTypes.join(",")}
          onChange={handleFileSelect}
        />

        <div
          className={cn(
            "relative rounded-2xl p-12 text-center transition-colors min-h-[280px] flex flex-col",
            invalidFiles.length && "border-red-500 border-2 bg-red-500/20"
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="flex-1 flex flex-col items-center justify-center">
            <h3 className="text-xl font-semibold mb-2">Drop your files here</h3>
            <p className="text-xs text-theme-2 mt-1">
              Max {(maxFileSizeBytes / 1024 / 1024).toFixed(0)}MB
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-3 mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-3xl gap-1 bg-theme-0 border-[0.5px] border-opacity-30 hover:brightness-85 transition-all"
            >
              <Upload />
              Upload Files
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenLinkDialog?.();
              }}
              className="rounded-3xl gap-1 bg-theme-0 border-[0.5px] border-opacity-30 hover:brightness-85 transition-all"
            >
              <Link />
              Add Link
            </Button>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      type="button"
                      variant="outline"
                      disabled
                      className="rounded-3xl gap-1 bg-theme-0 border-[0.5px] border-opacity-30 cursor-not-allowed opacity-50"
                    >
                      <FolderOpen />
                      Google Drive
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className='mb-1'>
                  <span>Coming soon</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenPasteTextDialog?.();
              }}
              className="rounded-3xl gap-1 bg-theme-0 border-[0.5px] border-opacity-30 hover:brightness-85 transition-all"
            >
              <Clipboard />
              Paste Text
            </Button>
          </div>
        </div>
      </div>

      {invalidFiles.map((i, idx) => (
        <p key={idx} className="text-sm text-red-600">
          {i.message}
        </p>
      ))}

      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Selected files:</h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-theme-0 rounded-md px-3 min-w-0"
              >
                <div className="text-xs px-2 py-0.5 rounded bg-theme-1 text-theme-2 shrink-0">
                  FILE
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-theme-1">
                    {formatFileSize(file.size)}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFile(file)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Links */}
      {selectedLinks.map((link, index) => (
        <div
          key={`link-${index}`}
          className="flex items-center gap-2 bg-theme-0 rounded-md px-3 min-w-0"
        >
          {/* icon  */}
          <div className="text-xs px-2 py-0.5 rounded bg-theme-1 text-theme-2 shrink-0">
            LINK
          </div>

          <p className="flex-1 truncate text-sm min-w-0 w-full">{link.url}</p>

          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() =>
              setSelectedSources((prev) => prev.filter((x) => x != link))
            }
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}
